import React, { Component } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { SuiContainer } from '@splashthat/sui';

import { NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP, NODE_NAME_UTM_PARAMS } from 'common/entities/formNode/nodeNames';
import FormEditorNodeChildrenWrapper from './FormEditorNodeChildrenWrapper';
import FormEditorDndNodeContentWrapper from './FormEditorDndNodeContentWrapper';
import LayoutNodeContent from './LayoutNodeContent';
import LayoutNodeSubSection from './LayoutNodeSubSection';

import FormEditorNode from './FormEditorNode';
import getReservedInputNameLabel from '../../utils/getReservedInputNameLabel';

const noRenderChildrenNodes = {
    [NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP]: true,
    [NODE_NAME_UTM_PARAMS]: true,
};

const BASE_CLASS_NAME = 'LayoutNode';

/**
 * A node in the layout view.
 */
export default class LayoutNode extends Component {
    static propTypes = {
        // props passed down:
        siblingNodePosition: PropTypes.number,

        // props comming from connect():
        formNode: PropTypes.object.isRequired,
        formLogicRules: PropTypes.array,

        dragNodeId: PropTypes.string,
        dropNodeId: PropTypes.string,
        dropNodePosition: PropTypes.number,
        dragNodePosition: PropTypes.number,

        depth: PropTypes.number.isRequired,

        bootstrapInputTypeDisplayNames: PropTypes.object.isRequired,

        beginDragFormNode: PropTypes.func.isRequired,
        endDragFormNode: PropTypes.func.isRequired,
        hoverDropFormNode: PropTypes.func.isRequired,
        openEditFormNode: PropTypes.func.isRequired,
        openEditFormNodeLogic: PropTypes.func.isRequired,
        openFormTemplateLibrary: PropTypes.func.isRequired,
        duplicateFormNode: PropTypes.func.isRequired,
        removeFormNode: PropTypes.func.isRequired,
    };

    static defaultProps = {
        formLogicRules: [],
        dropNodePosition: null,
        dragNodePosition: null,
        dragNodeId: null,
        dropNodeId: null,
        siblingNodePosition: 0,
        parentNodeId: null,
    };

    constructor() {
        super();
        this.state = {
            isActive: false,
        };
    }

    handleActiveChange = (isActive) => {
        if (this.state.isActive !== isActive) {
            this.setState({ isActive });
        }
    };

    openEditFormNode = () => {
        this.props.openEditFormNode(this.props.formNode.id);
    };

    openEditFormNodeLogic = () => {
        this.props.openEditFormNodeLogic(this.props.formNode.id);
    };

    duplicateNode = () => {
        const { formNode, siblingNodePosition } = this.props;
        const parentNodeId = formNode.parent;
        this.props.duplicateFormNode(parentNodeId, formNode.id, siblingNodePosition + 1);
    };

    deleteNode = () => {
        this.props.removeFormNode(this.props.formNode.id);
    };

    getClassName() {
        const { formNode } = this.props;

        return [
            BASE_CLASS_NAME,
            this.state.isActive && `${BASE_CLASS_NAME}--is-active`,
            formNode.type && `${BASE_CLASS_NAME}--content-${formNode.type}`,
            !isEmpty(formNode.children) && `${BASE_CLASS_NAME}--has-children`,
        ]
            .filter((v) => v)
            .join(' ');
    }

    shouldRenderChildren() {
        return !noRenderChildrenNodes[this.props.formNode.name];
    }

    shouldComponentUpdate(nextProps, nextState) {
        return this.state !== nextState || !isEqual(this.props, nextProps);
    }

    render() {
        const { formNode, bootstrapInputTypeDisplayNames } = this.props;

        const hasChildren = this.shouldRenderChildren() && !isEmpty(formNode.children);

        return (
            <SuiContainer className={this.getClassName()}>
                <FormEditorDndNodeContentWrapper
                    formNode={formNode}
                    beginDragFormNode={this.props.beginDragFormNode}
                    endDragFormNode={this.props.endDragFormNode}
                    hoverDropFormNode={this.props.hoverDropFormNode}
                >
                    {(connectDragSource) => (
                        <LayoutNodeContent
                            formNode={formNode}
                            formLogicRules={this.props.formLogicRules}
                            reservedNodeName={getReservedInputNameLabel(this.props.formNode.name)}
                            bootstrapInputTypeDisplayNames={bootstrapInputTypeDisplayNames}
                            depth={this.props.depth}
                            connectDragSource={connectDragSource}
                            onClickEdit={this.openEditFormNode}
                            onActiveChange={this.handleActiveChange}
                            isActive={this.state.isActive}
                            onClickLogic={this.openEditFormNodeLogic}
                            onClickDuplicate={this.duplicateNode}
                            onClickDelete={this.deleteNode}
                            onClickAddTemplate={this.props.openFormTemplateLibrary}
                        />
                    )}
                </FormEditorDndNodeContentWrapper>

                <LayoutNodeSubSection formNode={formNode} />

                {hasChildren && (
                    <FormEditorNodeChildrenWrapper
                        formNodeIds={formNode.children}
                        dragNodeId={this.props.dragNodeId}
                        dropNodeId={this.props.dropNodeId}
                        dropNodePosition={this.props.dropNodePosition}
                        dragNodePosition={this.props.dragNodePosition}
                        depth={this.props.depth}
                    >
                        {(formNodeId, index) => (
                            <FormEditorNode
                                key={formNodeId}
                                depth={this.props.depth + 1}
                                formNodeId={formNodeId}
                                siblingNodePosition={index}
                            />
                        )}
                    </FormEditorNodeChildrenWrapper>
                )}
            </SuiContainer>
        );
    }
}
